import { J2Caution, J2Multiply, K1Generic } from '@e3dc-react/icons'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, IconButton, Link, List, ListItem, Typography, useTheme } from '@mui/material'
import React from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import ResponsiveModalDrawerContainer from 'src/Elements/Utilities/ResponsiveModalDrawerContainer'
import { makeStyles } from 'tss-react/mui'

const CUSTOM_XS_BREAKPOINT = 400

const useStyles = makeStyles()(theme => ({
  containerStyle: {
    '&  .MuiDialog-paper, & .MuiDrawer-paper': {
      padding: theme.spacing(4),
      height: 'auto',

      [theme.breakpoints.down('sm')]: {
        paddingTop: 0,
      },
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(4),
    },

    [theme.breakpoints.down(CUSTOM_XS_BREAKPOINT)]: {
      gap: theme.spacing(1),
    },
  },
  icon: {
    fontSize: 30,
  },
  closeButton: {
    color: 'inherit',
    marginLeft: 'auto',
    padding: 0,
  },
  markdownWrapper: {
    '& > p:last-of-type': {
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      overflowY: 'scroll',
    },
    maxHeight: '70%',
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down(CUSTOM_XS_BREAKPOINT)]: {
      fontSize: 14,
    },
  },
}))

/** This component is a modal drawer that displays help content for offline installation. */
const OfflineInstallationHelpModal: React.FC<{ isVisible: boolean; handleClose: () => void }> = ({ isVisible, handleClose }) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { t } = useCustomTranslation()

  return (
    <ResponsiveModalDrawerContainer isOpen={isVisible} modalMaxWidth="md" containerStyle={classes.containerStyle} onClose={handleClose}>
      <Box component="header" className={classes.header}>
        <Box display="flex" alignItems="center">
          <Typography variant="h2" mr={1}>
            {t('systemSettings.offline-installation-help-modal.title')}
          </Typography>
          <K1Generic className={classes.icon} />
          <IconButton onClick={handleClose} className={classes.closeButton}>
            <J2Multiply className={classes.icon} />
          </IconButton>
        </Box>
        <Typography variant="h4">{t('systemSettings.offline-installation-help-modal.sub-title')}</Typography>
      </Box>

      <Box className={classes.markdownWrapper}>
        <ReactMarkdown components={markdownComponents}>{t('systemSettings.offline-installation-help-modal.markdown')}</ReactMarkdown>
      </Box>
      <Alert
        severity="warning"
        text={t('systemSettings.offline-installation-help-modal.alert-text')}
        sx={{
          position: 'absolute',
          bottom: 12,
          mr: 4,
          border: 'none',
          alignItems: 'center',

          /* increases specificity to override muiTheme.ts config */
          '&&&': { boxShadow: theme.effectStyles.shadowStrong },
          '& p': {
            color: theme.palette.warning.dark,
          },
          '& .MuiAlert-message': {
            padding: 0,
          },

          [theme.breakpoints.down('sm')]: {
            px: theme.spacing(2),
          },
        }}
        customIcon={<J2Caution sx={{ color: 'warning.dark', fontSize: 30 }} />}
      />
    </ResponsiveModalDrawerContainer>
  )
}

export default OfflineInstallationHelpModal

export const markdownComponents: Components = {
  ul(props) {
    const { node, ...rest } = props
    return <List {...rest} />
  },
  ol(props) {
    const { node, ...rest } = props
    return <List component="ol" sx={{ listStyle: 'decimal', pl: 4 }} {...rest} />
  },
  li(props) {
    const { node, ...rest } = props
    return <ListItem sx={{ display: 'list-item' }} disablePadding {...rest} />
  },
  a(props) {
    const { node, ...rest } = props
    return <Link color="inherit" {...rest} />
  },
}
